import { useCallback, useState } from "react";
import { Menu, Transition } from "@headlessui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisVertical } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { useQueryClient } from "@tanstack/react-query";
import type { MissionDetail, MissionStage } from "@/services/api/agentic-v2";
import { Button } from "@/components/ui/button/Button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "@/components/ui/dialog/Dialog";
import { Switch } from "@/components/ui/switch/Switch";
import { useNotificationOptional } from "@/context/NotificationContext";
import { useCanMissionOps } from "@/hooks/useCanMissionOps";
import {
  completeMission,
  completeMissionStage,
  reopenMissionStage,
} from "@/services/api/agentic-v2/ops";
import { invalidateMissionDetail } from "@/services/api/agentic-v2/queries/patches";
import {
  missionUsesPerTaskStageGrouping,
  tasksForMissionTimelineStage,
} from "./missionTaskStageBuckets";

type PendingAction =
  | { kind: "complete_stage"; stageName: string; doneTaskId?: string | null }
  | { kind: "reopen_stage"; stageName: string }
  | { kind: "complete_mission" };

export interface MissionStageOpsMenuProps {
  mission: MissionDetail;
  stage: MissionStage;
  isLastStage: boolean;
}

function parseOpenTaskCount(err: unknown): number | null {
  if (!err || typeof err !== "object") return null;
  const response = (err as { response?: { data?: { detail?: unknown } } }).response;
  const detail = response?.data?.detail ?? (err as { detail?: unknown }).detail;
  if (!detail || typeof detail !== "object") return null;
  const count = (detail as { open_task_count?: unknown }).open_task_count;
  return typeof count === "number" ? count : null;
}

export function MissionStageOpsMenu({
  mission,
  stage,
  isLastStage,
}: MissionStageOpsMenuProps) {
  const canMissionOps = useCanMissionOps();
  const notification = useNotificationOptional();
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [openTaskCount, setOpenTaskCount] = useState<number | null>(null);
  const [reason, setReason] = useState("");
  const [force, setForce] = useState(false);
  const [spawnFollowups, setSpawnFollowups] = useState(false);
  const [busy, setBusy] = useState(false);

  const usePerTaskStages = missionUsesPerTaskStageGrouping(mission.tasks);
  const stageTasks = tasksForMissionTimelineStage(mission, stage.name, usePerTaskStages);
  const doneTaskInStage = stageTasks.find((t) => t.status === "done");

  const openConfirm = useCallback((action: PendingAction) => {
    setPending(action);
    setOpenTaskCount(null);
    setReason("");
    setForce(false);
    setSpawnFollowups(Boolean(action.kind === "complete_stage" && doneTaskInStage));
    setConfirmOpen(true);
  }, [doneTaskInStage]);

  const runAction = useCallback(async () => {
    if (!pending) return;
    setBusy(true);
    const clientBody = mission.client_id ? { client_id: mission.client_id } : {};
    try {
      if (pending.kind === "complete_stage") {
        await completeMissionStage(mission._id, pending.stageName, {
          ...clientBody,
          force,
          reason: reason.trim() || undefined,
          spawn_followups_from_task_id:
            spawnFollowups && doneTaskInStage ? doneTaskInStage._id : undefined,
        });
        notification?.showSuccess("Stage completed");
      } else if (pending.kind === "reopen_stage") {
        await reopenMissionStage(mission._id, pending.stageName, {
          ...clientBody,
          reason: reason.trim() || undefined,
        });
        notification?.showSuccess("Stage reopened");
      } else {
        await completeMission(mission._id, {
          ...clientBody,
          force,
          reason: reason.trim() || undefined,
        });
        notification?.showSuccess("Mission completed");
      }
      invalidateMissionDetail(queryClient, mission._id);
      setConfirmOpen(false);
      setPending(null);
    } catch (err: unknown) {
      const count = parseOpenTaskCount(err);
      if (count !== null && !force) {
        setOpenTaskCount(count);
        setForce(true);
        notification?.showError(
          `${count} open task${count === 1 ? "" : "s"} remain — confirm force to continue`,
        );
      } else {
        notification?.showError(err instanceof Error ? err.message : "Action failed");
      }
    } finally {
      setBusy(false);
    }
  }, [
    pending,
    mission,
    force,
    reason,
    spawnFollowups,
    doneTaskInStage,
    notification,
    queryClient,
  ]);

  if (!canMissionOps) return null;

  const canCompleteStage =
    stage.status === "active" || stage.status === "pending";
  const canReopenStage =
    stage.status === "completed" ||
    mission.status === "completed" ||
    mission.status === "archived";
  const canCompleteMission =
    isLastStage && mission.status !== "completed" && mission.status !== "archived";

  const confirmTitle =
    pending?.kind === "complete_stage"
      ? "Complete stage?"
      : pending?.kind === "reopen_stage"
        ? "Reopen stage?"
        : "Complete mission?";

  const confirmBody =
    pending?.kind === "reopen_stage"
      ? "This updates mission stage metadata only. Downstream tasks are not deleted."
      : pending?.kind === "complete_mission"
        ? "This marks the mission completed and may trigger completion side effects (triggers, Guardians screening)."
        : "This marks the stage completed and advances the mission to the next stage.";

  return (
    <>
      <Menu as="div" className="relative shrink-0">
        <Menu.Button
          type="button"
          className="flex h-7 w-7 items-center justify-center rounded-md text-slate-500 hover:bg-white/10 hover:text-slate-200"
          aria-label="Stage admin actions"
          data-testid={`mission-stage-ops-menu-${stage.name}`}
          onClick={(event) => event.stopPropagation()}
        >
          <FontAwesomeIcon icon={faEllipsisVertical} className="h-3.5 w-3.5" />
        </Menu.Button>
        <Transition
          enter="transition duration-100 ease-out"
          enterFrom="transform scale-95 opacity-0"
          enterTo="transform scale-100 opacity-100"
          leave="transition duration-75 ease-out"
          leaveFrom="transform scale-100 opacity-100"
          leaveTo="transform scale-95 opacity-0"
        >
          <Menu.Items
            className="absolute right-0 top-full z-50 mt-1 w-52 rounded-lg border border-slate-600 bg-slate-800 p-1 shadow-lg focus:outline-none"
            onClick={(event) => event.stopPropagation()}
          >
            <Menu.Item disabled={!canCompleteStage}>
              {({ active, disabled }: { active: boolean; disabled: boolean }) => (
                <button
                  type="button"
                  disabled={disabled}
                  className={`w-full rounded px-3 py-2 text-left text-sm ${
                    active && !disabled ? "bg-slate-700" : ""
                  } ${disabled ? "text-slate-500" : "text-slate-200"}`}
                  onClick={() => openConfirm({ kind: "complete_stage", stageName: stage.name })}
                >
                  Complete stage
                </button>
              )}
            </Menu.Item>
            <Menu.Item disabled={!canReopenStage}>
              {({ active, disabled }: { active: boolean; disabled: boolean }) => (
                <button
                  type="button"
                  disabled={disabled}
                  className={`w-full rounded px-3 py-2 text-left text-sm ${
                    active && !disabled ? "bg-slate-700" : ""
                  } ${disabled ? "text-slate-500" : "text-slate-200"}`}
                  onClick={() => openConfirm({ kind: "reopen_stage", stageName: stage.name })}
                >
                  Reopen stage
                </button>
              )}
            </Menu.Item>
            {canCompleteMission ? (
              <Menu.Item>
                {({ active }: { active: boolean }) => (
                  <button
                    type="button"
                    className={`w-full rounded px-3 py-2 text-left text-sm ${
                      active ? "bg-slate-700" : ""
                    } text-slate-200`}
                    onClick={() => openConfirm({ kind: "complete_mission" })}
                  >
                    Complete mission
                  </button>
                )}
              </Menu.Item>
            ) : null}
          </Menu.Items>
        </Transition>
      </Menu>

      <Dialog open={confirmOpen} onClose={() => !busy && setConfirmOpen(false)} size="md">
        <DialogTitle>{confirmTitle}</DialogTitle>
        <DialogBody>
          <p className="text-sm text-slate-300 mb-3">{confirmBody}</p>
          {openTaskCount !== null ? (
            <p className="text-sm text-amber-300 mb-3">
              {openTaskCount} open task{openTaskCount === 1 ? "" : "s"} remain. Force this
              action to continue anyway.
            </p>
          ) : null}
          {pending?.kind === "complete_stage" && doneTaskInStage ? (
            <label className="flex items-center gap-2 text-sm text-slate-300 mb-3">
              <Switch checked={spawnFollowups} onChange={setSpawnFollowups} />
              Re-run branching from done task
            </label>
          ) : null}
          {(openTaskCount !== null ||
            pending?.kind === "complete_mission" ||
            pending?.kind === "complete_stage") && (
            <label className="flex items-start gap-2 text-sm text-amber-300 mb-3">
              <Switch checked={force} onChange={setForce} className="mt-0.5" />
              <span>Force (ignore open tasks)</span>
            </label>
          )}
          <label className="block text-sm text-slate-400 mb-1">Reason (optional)</label>
          <textarea
            className="w-full rounded-lg border border-slate-600 bg-slate-900 px-3 py-2 text-sm text-slate-100"
            rows={2}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Why is this override needed?"
          />
        </DialogBody>
        <DialogActions>
          <Button type="button" outline disabled={busy} onClick={() => setConfirmOpen(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            color="indigo"
            disabled={busy || (openTaskCount !== null && !force)}
            onClick={() => void runAction()}
          >
            {busy ? "Saving…" : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
